// src/admin/AdminJourneyJourneys.js

import React, { useState, useMemo } from 'react';

function AdminJourneyJourneys({
  journeys,
  selectedJourney,
  handleJourneyInputChange,
  handleSaveJourneyChanges,
  handleEditJourney,
  handleDeleteJourney,
  routes,
  getAssociatedRoutes,
  users,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('code');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  // const [selectedJourneyForCalendar, setSelectedJourneyForCalendar] = useState(null);
  const journeysPerPage = 10;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // Filter and sort journeys
  const filteredAndSortedJourneys = useMemo(() => {
    let filtered = journeys.filter(journey => {
      const searchLower = searchTerm.toLowerCase();
      const routeNames = getAssociatedRoutes(journey).toLowerCase();
      const user = users.find(u => u.id === journey.userId);
      const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase() : '';
      
      return (journey.code && journey.code.toLowerCase().includes(searchLower)) ||
             routeNames.includes(searchLower) ||
             userName.includes(searchLower) ||
             (journey.startDate && journey.startDate.includes(searchTerm));
    });

    return filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'code':
          aValue = (a.code || '').toLowerCase();
          bValue = (b.code || '').toLowerCase();
          break;
        case 'days':
          aValue = a.days || 0;
          bValue = b.days || 0;
          break;
        case 'cost':
          aValue = a.cost || 0;
          bValue = b.cost || 0;
          break;
        case 'paymentStatus':
          aValue = a.paymentStatus ? 1 : 0;
          bValue = b.paymentStatus ? 1 : 0;
          break;
        case 'startDate':
          aValue = a.startDate || '';
          bValue = b.startDate || '';
          break;
        case 'user':
          const userA = users.find(u => u.id === a.userId);
          const userB = users.find(u => u.id === b.userId);
          aValue = userA ? `${userA.firstName || ''} ${userA.lastName || ''}`.toLowerCase() : '';
          bValue = userB ? `${userB.firstName || ''} ${userB.lastName || ''}`.toLowerCase() : '';
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [journeys, users, searchTerm, sortField, sortDirection, getAssociatedRoutes]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedJourneys.length / journeysPerPage);
  const startIndex = (currentPage - 1) * journeysPerPage;
  const endIndex = startIndex + journeysPerPage;
  const currentJourneys = filteredAndSortedJourneys.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Reset to page 1 when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);
  return (
    <div style={{ marginBottom: '30px' }}>
      <h3>Manage Journeys</h3>
      <div>
        <label>
          Journey Code:
          <input
            type="text"
            name="code"
            value={selectedJourney.code}
            onChange={handleJourneyInputChange}
          />
        </label>

        <label>
          Days:
          <input
            type="number"
            name="days"
            value={selectedJourney.days}
            onChange={handleJourneyInputChange}
          />
        </label>

        <label>
          Cost:
          <input
            type="number"
            name="cost"
            value={selectedJourney.cost}
            onChange={handleJourneyInputChange}
          />
        </label>

        <label>
          Payment Status:
          <input
            type="checkbox"
            name="paymentStatus"
            checked={selectedJourney.paymentStatus}
            onChange={handleJourneyInputChange}
          />
        </label>

        <label>
          Routes:
          <select
            name="routeIds"
            value={selectedJourney.routeIds}
            onChange={(e) => {
              const opts = e.target.options;
              const selectedIds = [];
              for (let i = 0; i < opts.length; i++) {
                if (opts[i].selected) {
                  selectedIds.push(opts[i].value);
                }
              }
              handleJourneyInputChange({
                target: { name: 'routeIds', value: selectedIds },
              });
            }}
            multiple
          >
            {routes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          Start Date:
          <input
            type="date"
            name="startDate"
            value={selectedJourney.startDate}
            onChange={handleJourneyInputChange}
          />
        </label>

        {/* Optional: select user */}
        <label>
          User:
          <select
            name="userId"
            value={selectedJourney.userId || ''}
            onChange={handleJourneyInputChange}
          >
            <option value="">(None)</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstName} {u.lastName} ({u.email})
              </option>
            ))}
          </select>
        </label>

        <button onClick={handleSaveJourneyChanges}>
          {selectedJourney.id ? 'Update Journey' : 'Add Journey'}
        </button>
      </div>

      <h4 style={{
        fontSize: isMobile ? '16px' : '18px',
        marginTop: isMobile ? '20px' : '25px',
        marginBottom: isMobile ? '10px' : '15px'
      }}>Current Journeys</h4>
      
      {/* Search and Sort Controls */}
      <div style={{ 
        marginBottom: isMobile ? '10px' : '15px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '8px' : '10px',
        alignItems: isMobile ? 'stretch' : 'center'
      }}>
        <input
          type="text"
          placeholder="Search journeys..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: isMobile ? '6px' : '8px',
            fontSize: isMobile ? '13px' : '14px',
            border: '1px solid #ccc',
            borderRadius: '3px',
            flex: isMobile ? 'none' : '1',
            maxWidth: isMobile ? 'none' : '300px'
          }}
        />
        <div style={{ 
          fontSize: isMobile ? '12px' : '14px',
          color: '#666',
          whiteSpace: 'nowrap'
        }}>
          Page {currentPage} of {totalPages} ({filteredAndSortedJourneys.length} of {journeys.length} journeys)
        </div>
      </div>
      
      <div style={{ 
        overflowX: 'auto',
        marginLeft: isMobile ? '-15px' : '0',
        marginRight: isMobile ? '-15px' : '0',
        paddingLeft: isMobile ? '15px' : '0',
        paddingRight: isMobile ? '15px' : '0',
        WebkitOverflowScrolling: 'touch',
        width: isMobile ? 'calc(100% + 30px)' : '100%'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: isMobile ? '11px' : '14px',
          minWidth: isMobile ? '800px' : 'auto'
        }}>
        <thead>
          <tr>
            <th 
              onClick={() => handleSort('code')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Code {sortField === 'code' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => handleSort('days')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Days {sortField === 'days' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => handleSort('cost')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Cost {sortField === 'cost' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => handleSort('paymentStatus')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Payment {sortField === 'paymentStatus' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>Routes</th>
            <th 
              onClick={() => handleSort('startDate')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Start Date {sortField === 'startDate' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => handleSort('user')}
              style={{ 
                padding: isMobile ? '4px 3px' : '8px', 
                border: '1px solid #ddd', 
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              User {sortField === 'user' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', backgroundColor: '#f5f5f5' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentJourneys.map((j) => {
            const routeNames = getAssociatedRoutes(j);
            const user = users.find(u => u.id === j.userId);
            const userName = user ? `${user.firstName || ''} ${user.lastName || ''}` : '';
            
            return (
              <tr key={j.id}>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>{j.code}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', textAlign: 'center' }}>{j.days}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', textAlign: 'right' }}>{j.cost}</td>
                <td style={{ 
                  padding: isMobile ? '4px 3px' : '8px', 
                  border: '1px solid #ddd',
                  color: j.paymentStatus ? '#4CAF50' : '#f44336',
                  fontWeight: 'bold'
                }}>
                  {j.paymentStatus ? 'Paid' : 'Unpaid'}
                </td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', maxWidth: isMobile ? '80px' : '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{routeNames}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd' }}>{j.startDate || ''}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', maxWidth: isMobile ? '80px' : '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName}</td>
                <td style={{ padding: isMobile ? '4px 3px' : '8px', border: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                  <button 
                    onClick={() => handleEditJourney(j)}
                    style={{
                      padding: isMobile ? '3px 5px' : '5px 8px',
                      marginRight: '4px',
                      fontSize: isMobile ? '10px' : '12px',
                      border: '1px solid #ccc',
                      borderRadius: '2px',
                      backgroundColor: '#f8f9fa',
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteJourney(j.id)}
                    style={{
                      padding: isMobile ? '3px 5px' : '5px 8px',
                      fontSize: isMobile ? '10px' : '12px',
                      border: '1px solid #dc3545',
                      borderRadius: '2px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginTop: '20px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            style={{
              padding: '8px 12px',
              backgroundColor: currentPage === 1 ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? '12px' : '14px'
            }}
          >
            Previous
          </button>

          {/* Page Numbers */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <button
                key={pageNum}
                onClick={() => goToPage(pageNum)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === pageNum ? '#007bff' : '#fff',
                  color: currentPage === pageNum ? 'white' : '#007bff',
                  border: '1px solid #007bff',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '12px' : '14px'
                }}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            style={{
              padding: '8px 12px',
              backgroundColor: currentPage === totalPages ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: isMobile ? '12px' : '14px'
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminJourneyJourneys;
